import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import "../app.css";
import { api, Header, Spinner, ErrorNote, Field, inputCls, btnPrimary, btnGhost, DatePicker, friendlyDate } from "../shared";

interface Service {
  slug: string;
  name: string;
  duration_minutes: number;
  fee_text: string;
  summary: string;
}

interface Slot {
  startUtc: string;
  label: string;
}

type Step = "service" | "time" | "details" | "done";

function BookApp() {
  const [services, setServices] = useState<Service[] | null>(null);
  const [step, setStep] = useState<Step>("service");
  const [service, setService] = useState<Service | null>(null);
  const [date, setDate] = useState<string>("");
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<{ when: string } | null>(null);

  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", documentCount: "1", notes: "" });

  useEffect(() => {
    api<{ ok: boolean; services: Service[] }>("/api/services").then((r) => {
      if (r.ok) {
        setServices(r.services);
        const preselect = new URLSearchParams(location.search).get("service");
        const match = r.services.find((s) => s.slug === preselect);
        if (match) {
          setService(match);
          setStep("time");
        }
      } else setError("Could not load services. Please refresh or call us.");
    });
  }, []);

  useEffect(() => {
    if (!service || !date) return;
    setSlots(null);
    setSlot(null);
    api<{ ok: boolean; slots: Slot[]; error?: string }>(
      `/api/availability?service=${encodeURIComponent(service.slug)}&date=${encodeURIComponent(date)}`,
    ).then((r) => {
      if (r.ok) setSlots(r.slots);
      else setError(r.error ?? "Could not load availability.");
    });
  }, [service, date]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!service || !slot) return;
    setBusy(true);
    setError("");
    const r = await api<{ ok: boolean; when?: string; error?: string }>("/api/bookings", {
      method: "POST",
      body: JSON.stringify({
        serviceSlug: service.slug,
        startUtc: slot.startUtc,
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        documentCount: Number(form.documentCount) || 1,
        notes: form.notes,
      }),
    });
    setBusy(false);
    if (r.ok) {
      setConfirmation({ when: r.when! });
      setStep("done");
    } else {
      setError(r.error ?? "Something went wrong. Please try again or call us.");
      if (r.error?.includes("no longer available")) {
        // refresh slots so the taken one disappears
        setSlot(null);
        const rr = await api<{ ok: boolean; slots: Slot[] }>(
          `/api/availability?service=${service.slug}&date=${date}`,
        );
        if (rr.ok) setSlots(rr.slots);
      }
    }
  }

  const stepIndex = { service: 1, time: 2, details: 3, done: 4 }[step];

  return (
    <div className="min-h-screen bg-paper-alt">
      <Header title="Book an Appointment" />
      <main className="mx-auto max-w-3xl px-5 py-8">
        {step !== "done" && (
          <nav aria-label="Progress" className="mb-6 text-sm font-semibold text-ink-soft">
            <ol className="flex gap-2 p-0" style={{ listStyle: "none" }}>
              {["Service", "Date & time", "Your details"].map((label, i) => (
                <li key={label} className={i + 1 <= stepIndex ? "text-brand" : ""}>
                  {i + 1}. {label}
                  {i < 2 ? <span className="mx-2 text-slate-300">→</span> : null}
                </li>
              ))}
            </ol>
          </nav>
        )}
        <ErrorNote message={error} />

        {step === "service" && (
          <section aria-labelledby="pick-service">
            <h2 id="pick-service" className="mb-1 text-2xl font-bold">What do you need notarized?</h2>
            <p className="mb-5 text-ink-soft">Payment is collected in person at the appointment — nothing to pay online.</p>
            {!services ? (
              <Spinner />
            ) : (
              <ul className="grid gap-3 p-0" style={{ listStyle: "none" }}>
                {services.map((s) => (
                  <li key={s.slug}>
                    <button
                      className="w-full cursor-pointer rounded-xl border border-slate-300 bg-white px-5 py-4 text-left hover:border-brand"
                      onClick={() => {
                        setService(s);
                        setStep("time");
                        setError("");
                      }}
                    >
                      <span className="block font-bold">{s.name}</span>
                      <span className="block text-sm text-ink-soft">{s.summary}</span>
                      <span className="mt-1 block text-sm font-semibold text-accent">
                        About {s.duration_minutes} min · {s.fee_text}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {step === "time" && service && (
          <section aria-labelledby="pick-time">
            <h2 id="pick-time" className="mb-1 text-2xl font-bold">{service.name}</h2>
            <p className="mb-4 text-ink-soft">
              About {service.duration_minutes} minutes · {service.fee_text} ·{" "}
              <button className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-brand underline" onClick={() => { setStep("service"); setSlot(null); setDate(""); }}>
                change service
              </button>
            </p>
            <div className="grid gap-5 sm:grid-cols-[auto_1fr]">
              <div>
                <p className="mb-2 font-bold">Pick a date</p>
                <DatePicker serviceSlug={service.slug} value={date} onChange={(d) => setDate(d)} />
                <p className="mt-2 max-w-xs text-xs text-ink-soft">
                  All times are Eastern. Online booking needs 2+ hours' notice — for sooner, call (301) 915-4287.
                </p>
              </div>
              <div aria-live="polite">
                {!date ? (
                  <p className="rounded-xl bg-white px-5 py-6 text-ink-soft shadow-sm">
                    Choose a day on the calendar to see open times.
                  </p>
                ) : (
                  <div className="rounded-xl bg-white p-5 shadow-sm">
                    <p className="mb-3 font-bold" style={{ fontFamily: "Fraunces, Georgia, serif" }}>
                      {friendlyDate(date)}
                      <span className="ml-2 text-sm font-semibold text-ink-soft">— tap another day to change</span>
                    </p>
                    {slots === null ? (
                      <Spinner label="Checking availability…" />
                    ) : slots.length === 0 ? (
                      <p className="m-0 text-ink-soft">
                        No open times that day — pick another date, or call us and we may be able to fit you in.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 lg:grid-cols-4">
                        {slots.map((s) => (
                          <button
                            key={s.startUtc}
                            className={
                              "cursor-pointer rounded-lg border px-2 py-2 font-semibold " +
                              (slot?.startUtc === s.startUtc
                                ? "border-brand bg-brand text-white"
                                : "border-slate-300 bg-white text-ink hover:border-brand")
                            }
                            aria-pressed={slot?.startUtc === s.startUtc}
                            onClick={() => setSlot(s)}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {date && slot && (
              <p className="mt-4 rounded-lg border border-amber-900/20 bg-white px-4 py-2.5 font-semibold">
                Selected: {friendlyDate(date)} at {slot.label}
              </p>
            )}
            <div className="mt-5 flex gap-3">
              <button className={btnGhost} onClick={() => setStep("service")}>Back</button>
              <button className={btnPrimary} disabled={!slot} onClick={() => setStep("details")}>
                Continue
              </button>
            </div>
          </section>
        )}

        {step === "details" && service && slot && (
          <section aria-labelledby="your-details">
            <h2 id="your-details" className="mb-1 text-2xl font-bold">Your details</h2>
            <p className="mb-5 text-ink-soft">
              {service.name} · {friendlyDate(date)} at {slot.label}{" "}
              <button className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-brand underline" onClick={() => setStep("time")}>
                change time
              </button>
            </p>
            <form onSubmit={submit} className="grid gap-4 rounded-xl bg-white p-5 shadow-sm">
              <Field label="Full name">
                <input className={inputCls} required maxLength={120} value={form.name} autoComplete="name"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Phone">
                  <input className={inputCls} required type="tel" maxLength={25} value={form.phone} autoComplete="tel"
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </Field>
                <Field label="Email" hint="Your confirmation and reminder go here.">
                  <input className={inputCls} required type="email" maxLength={254} value={form.email} autoComplete="email"
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Field>
              </div>
              <Field label="Meeting address" hint="We come to you — home, office, hospital, or anywhere in Montgomery County.">
                <input className={inputCls} required minLength={8} maxLength={300} value={form.address} autoComplete="street-address"
                  onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Number of documents">
                  <input className={inputCls} required type="number" min={1} max={100} value={form.documentCount}
                    onChange={(e) => setForm({ ...form, documentCount: e.target.value })} />
                </Field>
              </div>
              <Field label="Notes (optional)" hint="Gate codes, parking, witnesses needed, anything we should know.">
                <textarea className={inputCls} rows={3} maxLength={1000} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
              <div className="flex gap-3">
                <button type="button" className={btnGhost} onClick={() => setStep("time")}>Back</button>
                <button type="submit" className={btnPrimary} disabled={busy}>
                  {busy ? "Booking…" : "Confirm appointment"}
                </button>
              </div>
              <p className="m-0 text-xs text-ink-soft">
                No payment is taken online. The notary collects payment in person once the work is done.
              </p>
            </form>
          </section>
        )}

        {step === "done" && confirmation && (
          <section aria-labelledby="confirmed" className="rounded-xl bg-white p-8 text-center shadow-sm">
            <h2 id="confirmed" className="mb-2 text-2xl font-bold text-green-700">You're booked!</h2>
            <p className="text-lg">
              <strong>{service?.name}</strong>
              <br />
              {confirmation.when} (Eastern)
            </p>
            <p className="text-ink-soft">
              A confirmation is on its way to your email and phone. Bring a valid photo ID and keep your documents{" "}
              <em>unsigned</em> until the notary arrives.
            </p>
            <p className="text-ink-soft">
              Need to change something? Manage your appointment at{" "}
              <a href="/account" className="font-semibold text-brand">/account</a> or call (301) 915-4287.
            </p>
            <a href="/" className={btnGhost}>Back to home</a>
          </section>
        )}
      </main>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<BookApp />);
